// @flow

import React, { PureComponent } from 'react';
import { graphql, QueryRenderer } from 'react-relay';
import styled from 'styled-components';
import { i18n } from 'shared/utils';
import relayEnvironment from 'shared/gql/relayEnvironment';
import Loader from 'shared/components/common/Loader';
import Button from 'shared/components/common/Button';
import * as Actions from 'main-app/store/Actions';
import UpdateCompanyMutation from 'main-app/mutations/UpdateCompany';

const Container = styled.div`
  padding: 24px;
  max-width: 600px;
`;

const Title = styled.h3`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 16px;
`;

const Description = styled.p`
  font-size: 13px;
  color: #666;
  margin-bottom: 24px;
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 24px;
`;

const RadioOption = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 12px;
  border: 1px solid ${p => (p.selected ? '#1890ff' : '#e8e8e8')};
  border-radius: 6px;
  cursor: pointer;
  background: ${p => (p.selected ? '#f0f7ff' : '#fff')};

  &:hover {
    border-color: #1890ff;
  }
`;

const RadioText = styled.div`
  flex: 1;
`;

const RadioTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 2px;
`;

const RadioDesc = styled.div`
  font-size: 12px;
  color: #888;
`;

const PICK_METHODS = [
  {
    value: 'FIFO',
    title: 'FIFO — First In, First Out',
    description: 'Pick the oldest inventory (earliest received) first.',
  },
  {
    value: 'LIFO',
    title: 'LIFO — Last In, First Out',
    description: 'Pick the newest inventory (most recently received) first.',
  },
  {
    value: 'FEFO',
    title: 'FEFO — First Expired, First Out',
    description: 'Pick inventory with the soonest expiration date first.',
  },
  {
    value: 'PICK_TO_CLEAN',
    title: 'Pick to Clean',
    description:
      'Pick from the location with the least inventory first to free up space.',
  },
];

type State = {
  selectedMethod: ?string,
  saving: boolean,
};

class PickingSettings extends PureComponent<{}, State> {
  state = {
    selectedMethod: null,
    saving: false,
  };

  handleSave = async (companyId: string) => {
    const { selectedMethod } = this.state;
    if (!selectedMethod) return;

    this.setState({ saving: true });

    try {
      await UpdateCompanyMutation.commit({
        variables: {
          input: { pickMethod: selectedMethod },
        },
      });
      Actions.alertNotification(
        i18n.t('Pick method updated successfully.'),
        'success',
      );
    } catch (e) {
      Actions.alertNotification(e.message, 'error');
    }

    this.setState({ saving: false });
  };

  render() {
    const { selectedMethod, saving } = this.state;

    return (
      <QueryRenderer
        environment={relayEnvironment}
        cacheConfig={{ force: true }}
        query={graphql`
          query PickingSettingsQuery {
            viewer {
              id
              company {
                id
                pickMethod
              }
            }
          }
        `}
        variables={{}}
        render={({ props }) => {
          if (!props) return <Loader />;

          const currentMethod =
            selectedMethod || props.viewer.company.pickMethod || 'FIFO';

          return (
            <Container>
              <Title>{i18n.t('Picking Method')}</Title>
              <Description>
                {i18n.t(
                  'Choose how auto-pick selects which lot to pick from when fulfilling shipments. This follows the Fishbowl-style 4-level hierarchy: (1) Manual override, (2) Default item location, (3) Expiration date, (4) Pick method below.',
                )}
              </Description>
              <RadioGroup>
                {PICK_METHODS.map(method => (
                  <RadioOption
                    key={method.value}
                    selected={currentMethod === method.value}
                    onClick={() =>
                      this.setState({ selectedMethod: method.value })
                    }
                  >
                    <input
                      type="radio"
                      checked={currentMethod === method.value}
                      onChange={() =>
                        this.setState({ selectedMethod: method.value })
                      }
                      style={{ marginTop: 2 }}
                    />
                    <RadioText>
                      <RadioTitle>{method.title}</RadioTitle>
                      <RadioDesc>{method.description}</RadioDesc>
                    </RadioText>
                  </RadioOption>
                ))}
              </RadioGroup>
              <Button
                theme="blue"
                loading={saving}
                onClick={() => this.handleSave(props.viewer.company.id)}
                disabled={!selectedMethod}
              >
                {i18n.t('Save Pick Method')}
              </Button>
            </Container>
          );
        }}
      />
    );
  }
}

export default PickingSettings;
